import { Component, EventEmitter, Input, Output, inject, computed, OnChanges, SimpleChanges, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DepartmentService } from '../../../../core/services/department.service';
import { ProjectService } from '../../../../core/services/project.service';
import { UserService } from '../../../../core/services/user.service';
import { LoadingService } from '../../../../core/services/loading.service';
import { TicketService } from '../../../../core/services/ticket.service';
import { ToastService } from '../../../../core/services/toast.service';
import { TicketPriority } from '../../../../core/models/ticket.model';
import { UserAvatarComponent } from '../../../../shared/components/avatar/user-avatar.component';
import { QuillModule } from 'ngx-quill';
import { AttachmentService } from '../../../../core/services/attachment.service';
import { forkJoin, of, switchMap } from 'rxjs';

@Component({
  selector: 'app-create-ticket-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, UserAvatarComponent, QuillModule],
  template: `
    @if (isOpen) {
      <div (paste)="onPaste($event)" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/75 backdrop-blur-xs animate-in fade-in">
        <div class="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl max-w-xl w-full p-6 shadow-2xl space-y-5 max-h-[90vh] overflow-y-auto">
          
          <!-- Header -->
          <div class="flex items-center justify-between border-b border-slate-200 dark:border-slate-800 pb-4">
            <div>
              <h3 class="text-base font-extrabold text-slate-900 dark:text-white tracking-tight">Create New Internal Ticket</h3>
              <p class="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Submit an internal request or operational issue.</p>
            </div>
            <button
              (click)="onCancel()"
              type="button"
              class="p-1 rounded-lg text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>

          <!-- Form Fields -->
          <form (ngSubmit)="onSubmit()" class="space-y-4 text-xs">
            
            <!-- Title -->
            <div class="space-y-1">
              <label class="block font-bold text-slate-700 dark:text-slate-300">Ticket Title <span class="text-rose-500">*</span></label>
              <input
                [(ngModel)]="title"
                name="title"
                required
                type="text"
                placeholder="e.g. Database latency spikes during peak morning hours"
                class="w-full bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl px-3.5 py-2.5 text-xs text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:outline-hidden focus:border-blue-500 transition-colors" />
            </div>

            <!-- Description -->
            <div class="space-y-1">
              <label class="block font-bold text-slate-700 dark:text-slate-300">Description <span class="text-rose-500">*</span></label>
              <quill-editor
                [(ngModel)]="description"
                name="description"
                [required]="true"
                placeholder="Describe the request, error details, steps to reproduce, or requirements..."
                class="block w-full bg-white dark:bg-slate-950 rounded-xl"
                [styles]="{height: '180px', border: '1px solid #cbd5e1', borderRadius: '0.75rem', overflow: 'hidden'}">
              </quill-editor>
            </div>

            <!-- Attachments Dropzone -->
            <div class="space-y-2">
              <div class="flex items-center justify-between">
                <label class="block font-bold text-slate-700 dark:text-slate-300">Attachments & Images</label>
                <span class="text-[11px] text-slate-400 font-mono">{{ selectedFiles.length }} file(s) attached</span>
              </div>

              <div
                (dragenter)="onDragEnter($event)"
                (dragover)="onDragOver($event)"
                (dragleave)="onDragLeave($event)"
                (drop)="onDropFiles($event)"
                (click)="fileInput.click()"
                [class.border-blue-500]="isDraggingFiles"
                [class.bg-blue-50]="isDraggingFiles"
                [class.dark:bg-blue-950]="isDraggingFiles"
                [class.dark:border-blue-500]="isDraggingFiles"
                class="border-2 border-dashed border-slate-300 dark:border-slate-700 rounded-xl p-4 text-center hover:border-blue-400 dark:hover:border-blue-500 hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-all cursor-pointer group"
              >
                <input
                  type="file"
                  multiple
                  #fileInput
                  class="hidden"
                  (change)="onFilesSelected($event)"
                />
                <div class="flex flex-col items-center justify-center space-y-1.5 pointer-events-none">
                  <div
                    [class.scale-110]="isDraggingFiles"
                    [class.text-blue-600]="isDraggingFiles"
                    [class.text-slate-400]="!isDraggingFiles"
                    class="w-9 h-9 rounded-xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center transition-all group-hover:scale-105 group-hover:text-blue-500"
                  >
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                  </div>
                  <p class="text-xs text-slate-600 dark:text-slate-300 font-medium">
                    <span class="text-blue-600 dark:text-blue-400 font-bold underline">Click to browse</span>, drag & drop, or paste clipboard image <kbd class="px-1.5 py-0.5 text-[10px] font-mono bg-slate-200 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded">Ctrl+V</kbd>
                  </p>
                  <p class="text-[10px] text-slate-400 dark:text-slate-500">Max 10MB per file (Screenshots, Images, PDFs, Docs, Archives)</p>
                </div>
              </div>

              @if (selectedFiles.length > 0) {
                <div class="flex flex-wrap gap-2 mt-2">
                  @for (f of selectedFiles; track f.name + f.size) {
                    <div class="flex items-center px-2.5 py-1.5 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-lg text-[11px] font-medium border border-blue-100 dark:border-blue-800/60 shadow-2xs group">
                      @if (isImageFile(f)) {
                        <svg class="w-3.5 h-3.5 mr-1.5 text-blue-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      } @else {
                        <svg class="w-3.5 h-3.5 mr-1.5 text-blue-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      }
                      <span class="truncate max-w-[140px] font-semibold">{{ f.name }}</span>
                      <span class="ml-1 text-[10px] text-blue-500/80 font-mono">({{ formatBytes(f.size) }})</span>
                      <button
                        type="button"
                        (click)="removeFile(f)"
                        aria-label="Remove attachment"
                        class="ml-2 text-blue-400 hover:text-rose-500 dark:hover:text-rose-400 transition-colors p-0.5 cursor-pointer"
                      >
                        <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
                      </button>
                    </div>
                  }
                </div>
              }
            </div>

            <!-- Department & Project Row -->
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
              
              <!-- Department -->
              <div class="space-y-1">
                <label class="block font-bold text-slate-700 dark:text-slate-300">Target Department <span class="text-rose-500">*</span></label>
                <select
                  [(ngModel)]="departmentId"
                  (ngModelChange)="onDeptChange()"
                  name="departmentId"
                  required
                  class="w-full bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl px-3 py-2.5 text-xs text-slate-900 dark:text-white focus:outline-hidden focus:border-blue-500">
                  <option value="" disabled selected>-- Select Department --</option>
                  @for (dept of assignableDepartments(); track dept.id) {
                    <option [value]="dept.id">{{ dept.name }} ({{ dept.code }})</option>
                  }
                  @if (departmentId && !hasDeptInList(departmentId)) {
                    <option [value]="departmentId">Department (ID: {{ departmentId }})</option>
                  }
                </select>
              </div>

              <!-- Project -->
              <div class="space-y-1">
                <label class="block font-bold text-slate-700 dark:text-slate-300">Associated Project</label>
                <select
                  [(ngModel)]="projectId"
                  (ngModelChange)="onProjChange()"
                  name="projectId"
                  [disabled]="!departmentId"
                  class="w-full bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl px-3 py-2.5 text-xs text-slate-900 dark:text-white focus:outline-hidden focus:border-blue-500 disabled:opacity-50">
                  <option [value]="undefined">None (General Request)</option>
                  @for (proj of filteredProjects(); track proj.id) {
                    <option [value]="proj.id">{{ proj.name }}</option>
                  }
                </select>
              </div>

            </div>

            <!-- Priority & Assignee Row -->
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
              
              <!-- Priority -->
              <div class="space-y-1">
                <label class="block font-bold text-slate-700 dark:text-slate-300">Priority Level <span class="text-rose-500">*</span></label>
                <select
                  [(ngModel)]="priority"
                  name="priority"
                  required
                  class="w-full bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl px-3 py-2.5 text-xs text-slate-900 dark:text-white focus:outline-hidden focus:border-blue-500">
                  <option value="Low">Low (General Queue)</option>
                  <option value="Medium">Medium (Normal Standard)</option>
                  <option value="High">High (Urgent Response)</option>
                  <option value="Critical">Critical (P1 System Outage)</option>
                </select>
              </div>

              <!-- Assignee (Filtered by Selected Department) -->
              <div class="space-y-1">
                <label class="block font-bold text-slate-700 dark:text-slate-300">Assignee (Department Members)</label>
                <select
                  [(ngModel)]="assigneeId"
                  name="assigneeId"
                  [disabled]="!departmentId"
                  class="w-full bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl px-3 py-2.5 text-xs text-slate-900 dark:text-white focus:outline-hidden focus:border-blue-500 disabled:opacity-50">
                  <option [value]="undefined">Auto-Assign (Default Rules)</option>
                  @for (user of filteredUsers(); track user.id) {
                    <option [value]="user.id">{{ user.name }} ({{ user.designation || 'Staff' }})</option>
                  }
                  @if (assigneeId && !hasAssigneeInList(assigneeId)) {
                    <option [value]="assigneeId">User (ID: {{ assigneeId }})</option>
                  }
                </select>
              </div>

            </div>

            <!-- Auto-Assignment Preview Callout -->
            @if (departmentId && !assigneeId && autoAssigneePreview()) {
              <div class="p-3 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-between text-xs">
                <div class="flex items-center space-x-2">
                  <svg class="w-4 h-4 text-blue-500 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                  <span class="text-blue-700 dark:text-blue-300 font-medium">Auto-Assignment Rule:</span>
                </div>
                @if (autoAssigneePreview()?.assignee) {
                  <div class="flex items-center space-x-1.5 font-bold text-slate-900 dark:text-white">
                    <app-user-avatar [name]="autoAssigneePreview()!.assignee!.name" [src]="autoAssigneePreview()!.assignee!.avatarUrl" size="xs" />
                    <span>{{ autoAssigneePreview()!.assignee!.name }}</span>
                  </div>
                } @else {
                  <span class="text-slate-500 dark:text-slate-400 italic">Will remain unassigned in queue</span>
                }
              </div>
            }

            <!-- Buttons -->
            <div class="flex items-center justify-end space-x-3 pt-4 border-t border-slate-200 dark:border-slate-800">
              <button
                (click)="onCancel()"
                type="button"
                class="px-4 py-2.5 rounded-xl font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
                Cancel
              </button>
              <button
                type="submit"
                [disabled]="!departmentId || !title.trim() || !description.trim() || isSubmitting || isGlobalLoading()"
                class="px-5 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold shadow-lg shadow-blue-600/30 transition-all flex items-center space-x-2">
                @if (isSubmitting || isGlobalLoading()) {
                  <svg class="animate-spin w-4 h-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path>
                  </svg>
                  <span>Creating...</span>
                } @else {
                  <span>Create Ticket</span>
                }
              </button>
            </div>

          </form>

        </div>
      </div>
    }
  `,
})
export class CreateTicketModalComponent implements OnChanges {
  private readonly departmentService = inject(DepartmentService);
  private readonly projectService = inject(ProjectService);
  private readonly userService = inject(UserService);
  readonly ticketService = inject(TicketService);
  private readonly loadingService = inject(LoadingService);
  private readonly attachmentService = inject(AttachmentService);
  private readonly toastService = inject(ToastService);

  readonly isGlobalLoading = this.loadingService.isLoading;

  @Input() isOpen = false;
  @Input() initialData?: {
    title?: string;
    description?: string;
    departmentId?: string;
    projectId?: string;
    priority?: TicketPriority;
    assigneeId?: string;
  } | null = null;

  @Output() close = new EventEmitter<void>();

  readonly departments = this.departmentService.departments;
  readonly assignableDepartments = computed(() => this.departments().filter((d) => d.isAssignTickets !== false));
  readonly projects = this.projectService.projects;
  readonly users = this.userService.users;

  title = '';
  description = '';
  departmentId = '';
  projectId: string | undefined = undefined;
  priority: TicketPriority = 'Medium';
  assigneeId: string | undefined = undefined;
  isSubmitting = false;
  selectedFiles: File[] = [];
  isDraggingFiles = false;
  private dragCounter = 0;

  @HostListener('window:paste', ['$event'])
  onWindowPaste(event: ClipboardEvent): void {
    if (!this.isOpen) return;
    this.handleClipboardPaste(event);
  }

  onPaste(event: ClipboardEvent): void {
    if (!this.isOpen) return;
    this.handleClipboardPaste(event);
  }

  private handleClipboardPaste(event: ClipboardEvent): void {
    const clipboardData = event.clipboardData;
    if (!clipboardData) return;

    const items = clipboardData.items;
    const files = clipboardData.files;
    const pastedImages: File[] = [];

    if (items && items.length > 0) {
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.type && item.type.startsWith('image/')) {
          const blob = item.getAsFile();
          if (blob) {
            const ext = blob.type.split('/')[1] || 'png';
            const now = new Date();
            const timestamp = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}_${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}${String(now.getSeconds()).padStart(2, '0')}`;
            const isGeneric = !blob.name || blob.name === 'image.png' || blob.name === 'blob';
            const fileName = isGeneric ? `pasted-image-${timestamp}.${ext}` : blob.name;
            const file = new File([blob], fileName, { type: blob.type });
            pastedImages.push(file);
          }
        }
      }
    } else if (files && files.length > 0) {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (file.type && file.type.startsWith('image/')) {
          pastedImages.push(file);
        }
      }
    }

    if (pastedImages.length > 0) {
      event.preventDefault();
      event.stopPropagation();
      for (const img of pastedImages) {
        this.selectedFiles.push(img);
      }
      this.toastService.info('Image Attached', `Pasted ${pastedImages.length} image(s) from clipboard`);
    }
  }

  isImageFile(file: File): boolean {
    return file?.type ? file.type.startsWith('image/') : /\.(jpg|jpeg|png|gif|webp|svg|bmp)$/i.test(file?.name || '');
  }

  onDragEnter(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragCounter++;
    this.isDraggingFiles = true;
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDraggingFiles = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragCounter--;
    if (this.dragCounter <= 0) {
      this.dragCounter = 0;
      this.isDraggingFiles = false;
    }
  }

  onDropFiles(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragCounter = 0;
    this.isDraggingFiles = false;

    if (event.dataTransfer?.files && event.dataTransfer.files.length > 0) {
      const files = event.dataTransfer.files;
      for (let i = 0; i < files.length; i++) {
        this.selectedFiles.push(files[i]);
      }
    }
  }

  formatBytes(bytes: number): string {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen']?.currentValue) {
      this.populateForm();
    }
  }

  populateForm(): void {
    const data = this.initialData || this.ticketService.createModalInitialData();
    if (data) {
      this.title = data.title || '';
      this.description = data.description || '';
      this.departmentId = data.departmentId ? String(data.departmentId) : '';
      this.projectId = data.projectId ? String(data.projectId) : undefined;
      this.priority = data.priority || 'Medium';
      this.assigneeId = data.assigneeId ? String(data.assigneeId) : undefined;
    } else {
      this.resetForm();
    }
  }

  hasDeptInList(deptId: string): boolean {
    return this.assignableDepartments().some((d) => String(d.id) === String(deptId));
  }

  hasAssigneeInList(uId: string): boolean {
    return this.filteredUsers().some((u) => String(u.id) === String(uId));
  }

  onFilesSelected(event: any): void {
    const files = event.target.files;
    if (files && files.length > 0) {
      for (let i = 0; i < files.length; i++) {
        this.selectedFiles.push(files[i]);
      }
    }
    // reset input
    event.target.value = '';
  }

  removeFile(file: File): void {
    this.selectedFiles = this.selectedFiles.filter(f => f !== file);
  }

  getSelectedDepartmentId(): string {
    return this.departmentId || '';
  }

  filteredProjects = () => {
    const activeDeptId = this.getSelectedDepartmentId();
    if (!activeDeptId) return [];
    return this.projects().filter((p) => String(p.departmentId) === String(activeDeptId));
  };

  filteredUsers = () => {
    const activeDeptId = this.getSelectedDepartmentId();
    const assignableStaff = this.users().filter((u) => u.isAssignTickets !== false);
    if (!activeDeptId) return assignableStaff;
    return assignableStaff.filter(
      (u) => !u.departmentId || String(u.departmentId) === String(activeDeptId) || (this.assigneeId && String(u.id) === String(this.assigneeId)),
    );
  };

  autoAssigneePreview = () => {
    const activeDeptId = this.getSelectedDepartmentId();
    if (!activeDeptId) return null;
    return this.ticketService.calculateAutoAssignee(activeDeptId, this.projectId);
  };

  onDeptChange(): void {
    this.projectId = undefined;
    if (!this.assigneeId || !this.initialData) {
      this.assigneeId = undefined;
    }
  }

  onProjChange(): void { }

  onSubmit(): void {
    if (!this.departmentId || !this.title.trim() || !this.description.trim() || this.isSubmitting) return;

    this.isSubmitting = true;

    this.ticketService.createTicket({
      title: this.title.trim(),
      description: this.description.trim(),
      departmentId: this.departmentId,
      projectId: this.projectId || undefined,
      priority: this.priority,
      assigneeId: this.assigneeId || undefined,
    }).pipe(
      switchMap(ticket => {
        if (this.selectedFiles.length > 0 && ticket?.id) {
          const uploadRequests = this.selectedFiles.map(file =>
            this.attachmentService.uploadToTicket(ticket.id, file)
          );
          return forkJoin(uploadRequests);
        }
        return of(null);
      })
    ).subscribe({
      next: () => {
        this.isSubmitting = false;
        this.resetForm();
        this.close.emit();
      },
      error: (_err) => {
        this.isSubmitting = false;
      },
    });
  }

  onCancel(): void {
    this.resetForm();
    this.close.emit();
  }

  private resetForm(): void {
    this.title = '';
    this.description = '';
    this.departmentId = '';
    this.projectId = undefined;
    this.priority = 'Medium';
    this.assigneeId = undefined;
    this.isSubmitting = false;
    this.selectedFiles = [];
  }
}
